import React, { FC, Fragment, useMemo } from 'react'
import { message } from 'antd'
import { Formik, FormikHelpers } from 'formik'
import { Form, Radio } from 'formik-antd'
import { useMutation, useQuery } from 'react-query'
import { TasksApi } from '@libs/api'
import { Autolocalize, FormikTextArea, UserInteraction } from '@libs/components'
import Styled from './Styled'
import QUERIES from 'modules/common/queries'
import { useLocalization } from '@libs/localization'
import { createValidationSchema } from '@libs/util'

export interface IFormValues {
	answers: string[]
}

export interface IQuestionnaireFormProps {
	taskId: string
	onSuccess?: () => void
	setIsLoading?: (isLoading: boolean) => void
	onError?: (error: string) => void
	onAlreadySubmitted?: () => void
	includeSubmitButton?: boolean
	setDisabledSubmitButton?: (disabled: boolean) => void
	setIsSubmitting?: (isSubmitting: boolean) => void
}

const StaticAnswer = {
	YES: 'Yes',
	NO: 'No',
} as const

const formSchema = createValidationSchema(yup =>
	yup.object().shape({
		answers: yup.array().of(yup.string()).required(),
	})
)

const QuestionnaireForm: FC<IQuestionnaireFormProps> = ({
	taskId,
	includeSubmitButton = true,
	onSuccess,
	onError,
	onAlreadySubmitted,
	setIsLoading,
	setDisabledSubmitButton,
	setIsSubmitting,
}) => {
	const { t } = useLocalization()

	const { data: { questions = [] } = {}, isLoading } = useQuery(
		[QUERIES.questionnaire, taskId],
		() => TasksApi.getAITextQuestionnaire(taskId),
		{
			enabled: !!taskId,
			onSuccess: response => {
				if (response?.questions?.length === 0) {
					onAlreadySubmitted?.()
				}
				if (!response) {
					onError?.('Error fetching the questionnaire')
					return
				}
				setIsLoading?.(false)
			},
			onError: (err: Error) => {
				const errorMessage = err.message
				onError?.(errorMessage)
			},
			staleTime: 0,
			cacheTime: 0,
		}
	)

	const { mutateAsync: saveQuestions, isLoading: isSubmitting } = useMutation(TasksApi.saveAITextQuestionnaire, {
		onError: (err: Error) => {
			const errorMessage = err.message
			message.error(errorMessage)
			onError?.(errorMessage)
		},
	})

	const onSubmit = async (values: IFormValues, { resetForm }: FormikHelpers<IFormValues>) => {
		if (questions) {
			await saveQuestions({
				taskId,
				answers: questions.map(({ question }, index) => ({
					question,
					answer: values.answers[index],
				})),
			})

			resetForm()
			onSuccess?.()
		}
	}

	const initialFormValues: IFormValues = useMemo(
		() => ({
			answers: questions.map(() => ''),
		}),
		[questions]
	)

	if (isLoading || !questions || questions.length === 0) {
		return null
	}

	return (
		<Formik<IFormValues> initialValues={initialFormValues} validationSchema={formSchema} onSubmit={onSubmit}>
			{({ isValid, isSubmitting: formIsSubmitting }) => {
				setDisabledSubmitButton?.(!isValid)
				setIsSubmitting?.(formIsSubmitting)

				return (
					<Form id="ai-text-details-questionnaire-form" layout="vertical">
						<Autolocalize />
						<UserInteraction enabled={!formIsSubmitting && !isSubmitting}>
							{questions.map(({ question, yesOrNo }, index) => (
								<Fragment key={index}>
									<Styled.FormItem name={`answers[${index}]`} label={question}>
										{yesOrNo ? (
											<Radio.Group name={`answers[${index}]`}>
												<Radio name={`answers[${index}]`} value={StaticAnswer.YES}>
													{t('common.terms.yes')}
												</Radio>
												<Radio name={`answers[${index}]`} value={StaticAnswer.NO}>
													{t('common.terms.no')}
												</Radio>
											</Radio.Group>
										) : (
											<FormikTextArea
												limitCharacters
												name={`answers[${index}]`}
												autoSize={{ minRows: 2, maxRows: 4 }}
											/>
										)}
									</Styled.FormItem>
								</Fragment>
							))}
						</UserInteraction>
						{includeSubmitButton && (
							<Styled.SubmitButton
								disabled={!isValid}
								loading={formIsSubmitting || isSubmitting}
								type="primary"
								size="large"
								shape="round"
							>
								{t('common.terms.submit')}
							</Styled.SubmitButton>
						)}
					</Form>
				)
			}}
		</Formik>
	)
}

export default QuestionnaireForm
